/**
 * Pino-based structured logger factory. ARCH-019.
 *
 * createLogger(surface) returns a Pino logger pre-configured with:
 *   - JSON transport in production, pretty-print in development
 *   - Pino browser-mode fallback for client bundles
 *   - The ARCH-019 envelope fields auto-populated via mixin:
 *       requestId  (from AsyncLocalStorage, ARCH-020)
 *       surface    (fixed for this logger instance)
 *       subscriberId / route  (from ALS store when present)
 *   - Log level from LOG_LEVEL env (default: info in prod, debug in dev)
 *
 * Usage:
 *   const logger = createLogger('portal');
 *   logger.info({ subscriberId }, 'Subscriber signed in');
 *   logger.error({ err }, 'Stripe call failed');
 */

import pino, { type Logger } from 'pino';
import type { RequestStore } from './store.js';

/**
 * Request-context provider for the ARCH-019 envelope. Defaults to a no-op so
 * the client never imports the Node-only store module (and node:async_hooks
 * never reaches a browser bundle). The server entrypoint (@studio/logger/
 * server) sets this to read from the AsyncLocalStorage store (ARCH-020).
 *
 * The import above is type-only and erased at compile time -- it creates no
 * runtime dependency on store.ts.
 */
let contextProvider: () => Partial<RequestStore> = () => ({});

/** Inject the request-context source. Called by @studio/logger/server. */
export function setLoggerContextProvider(
  provider: () => Partial<RequestStore>,
): void {
  contextProvider = provider;
}

/** Valid surface identifiers (ARCH-019 envelope). */
export type Surface =
  | 'portal'
  | 'operator'
  | 'mcp'
  | 'renderer'
  | 'webhook';

/** The ARCH-019 envelope fields on every log entry. */
export interface LogEnvelope {
  level: string;
  msg: string;
  time: number;
  requestId?: string;
  surface: Surface;
  subscriberId?: string;
  taskId?: string;
  route?: string;
  err?: unknown;
}

function resolveLevel(): string {
  const g = globalThis as {
    process?: { env?: Record<string, string | undefined> };
  };
  const fromEnv = g.process?.env?.['LOG_LEVEL'];
  if (fromEnv) return fromEnv;
  const nodeEnv = g.process?.env?.['NODE_ENV'];
  return nodeEnv === 'production' ? 'info' : 'debug';
}

/**
 * Creates a surface-scoped Pino logger that automatically populates
 * ARCH-019 envelope fields from AsyncLocalStorage (ARCH-020).
 *
 * Isomorphic: works on Node (JSON output to stdout) and in browser bundles
 * (pino's built-in browser shim dispatches to the browser console
 * internally -- no bare console.* call appears in this source tree).
 */
export function createLogger(surface: Surface): Logger {
  const level = resolveLevel();

  return pino({
    level,
    browser: {
      // Pino browser mode: let pino use its built-in console dispatch
      // internally. The ARCH-019 rule bans bare console.* calls in application
      // source; pino's own internal dispatch inside node_modules does not count.
      // asObject: false (default) so pino serializes and calls console[level]
      // itself -- no console.* call appears in our source tree.
      asObject: false,
    },
    mixin() {
      // ARCH-020 request-ID + ALS-sourced fields via the context provider.
      // No-op on the client (no ALS); reads the AsyncLocalStorage store on the
      // server once @studio/logger/server has wired the provider.
      const ctx = contextProvider();
      const extra: Record<string, string> = { surface };
      if (ctx.requestId) extra['requestId'] = ctx.requestId;
      if (ctx.subscriberId) extra['subscriberId'] = ctx.subscriberId;
      if (ctx.route) extra['route'] = ctx.route;
      return extra;
    },
    serializers: {
      err: pino.stdSerializers.err,
    },
    timestamp: pino.stdTimeFunctions.isoTime,
  });
}
